import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject
} from '@angular/core';

import { Subject, filter, takeUntil } from 'rxjs';

import { PrimaryMenuType, PrimaryNavItem } from '@hdms/shared/page-frame/types';
import { FlyoutDirective } from '@hdms/shared/ui-core';
import { HashTriggerService, SANITIZE_HASH_REGEX } from '@hdms/shared/util-core';

import { MenuStateService } from '../../../services';

@Component({
  selector: 'shrd-pf-ft-header-mega-menu-item',
  templateUrl: './header-mega-menu-item.component.html',
  styleUrls: ['./header-mega-menu-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class HeaderMegaMenuItemComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() item: PrimaryNavItem;

  @HostBinding('class.mega-menu-item') readonly hostClass = true;

  @ViewChild(FlyoutDirective) private readonly flyout: FlyoutDirective;

  isProductMenu: boolean;

  private readonly hashTriggerService = inject(HashTriggerService);
  private readonly stateService = inject(MenuStateService);
  private readonly zone = inject(NgZone);

  private readonly unsubscribe$ = new Subject<void>();

  ngAfterViewInit(): void {
    this.restoreMenuState();
    this.subscribeToHashChange();
    this.stateService.close$.pipe(takeUntil(this.unsubscribe$)).subscribe(_ => this.closeFlyout());
  }

  ngOnChanges({ item }: SimpleChanges): void {
    if (item) {
      this.isProductMenu = this.item.menuType === PrimaryMenuType.PRODUCTS;
    }
  }

  handleFlyoutVisibilityChange(isOpened: boolean): void {
    if (isOpened) {
      this.stateService.setCurrentMenuId(this.item.id);
    } else if (this.item.id === this.stateService.currentMenuId) {
      this.stateService.setCurrentMenuId(null);
    }
  }

  ngOnDestroy(): void {
    this.closeFlyout();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private closeFlyout(): void {
    this.flyout.close();
  }

  private restoreMenuState(): void {
    if (this.stateService.currentMenuId === this.item.id) {
      this.zone.run(() => this.flyout.open());
    }
  }

  private subscribeToHashChange(): void {
    const sanitizedHash = this.item.url?.replace(SANITIZE_HASH_REGEX, '').replace(/&.*/, '');
    if (sanitizedHash) {
      const pattern = sanitizedHash + '.*';
      const regex = new RegExp(pattern);
      this.hashTriggerService.registerHash(pattern);

      this.hashTriggerService.hashChange$
        .pipe(
          filter(hash => regex.test(hash)),
          takeUntil(this.unsubscribe$)
        )
        .subscribe(_ => this.flyout.open());
    }
  }
}
